import {
  faArrowLeft,
  faBookOpen,
  faClock,
  faDollarSign,
  faEdit,
  faFire,
  faLeaf,
  faStar,
  faUser,
  faUtensils,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchRecipeDetails } from '../components/api';
import { auth, isAuthor } from '../components/firebase';
import RelatedRecipes from '../components/RelatedRecipes';
import ReviewsSection, { useAverageRating } from '../components/ReviewsSection';
import '../styling/RecipeDetails.css';

function RecipeDetails() {
  const { recipeId } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user] = useAuthState(auth);
  const [isRecipeAuthor, setIsRecipeAuthor] = useState(false);
  const [clickedIngredients, setClickedIngredients] = useState([]);
  
  const { avgRating, reviewCount, loading: ratingLoading } = useAverageRating(recipeId);

  const handleIngredientClick = (index) => {
    if (clickedIngredients.includes(index)) {
      setClickedIngredients(clickedIngredients.filter((i) => i !== index));
    } else {
      setClickedIngredients([...clickedIngredients, index]);
    }
  };

  const difficultyLevel = (value) => {
    if (!value) return 0;
    if (typeof value === 'string') {
      const normalized = value.toLowerCase();
      if (normalized.includes('easy')) return 1;
      if (normalized.includes('medium') || normalized.includes('med')) return 2;
      if (normalized.includes('hard') || normalized.includes('difficult')) return 3;
    }
    if (typeof value === 'number') {
      if (value <= 1) return 1;
      if (value === 2) return 2;
      return 3;
    }
    return 0;
  };

  useEffect(() => {
    fetchRecipeDetails(recipeId)
      .then((data) => {
        if (data) {
          setRecipe(data);
          setIsRecipeAuthor(isAuthor(user, data.author?.uid));
        } else {
          setError('Recipe not found');
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching recipe details:', err);
        setError('Error fetching recipe details');
        setLoading(false);
      });
  }, [recipeId, user]);

  if (loading) {
    return <div className="loading-container">Loading...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  const level = difficultyLevel(recipe.difficulty);

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 transition"
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </button>
          <button
            onClick={() => recipe.author?.uid && navigate(`/profile/${recipe.author.uid}`)}
            className="text-gray-500 hover:text-orange-500 transition font-medium"
          >
            By @{recipe.author?.name || 'unknown'}
          </button>
        </div>
        {isRecipeAuthor && (
          <button
            className="edit-button btn btn-outline-primary btn-sm"
            onClick={() => {
              window.location = `/editRecipe/${recipeId}`;
            }}
          >
            <FontAwesomeIcon icon={faEdit} /> Edit
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-[1.1fr_0.9fr] gap-8 items-center mb-8">
        <div className="space-y-4">
          {!ratingLoading && reviewCount > 0 && (
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <FontAwesomeIcon
                    key={i}
                    icon={faStar}
                    className={i < Math.round(avgRating) ? 'text-yellow-400' : 'text-gray-300'}
                  />
                ))}
              </div>
              <span className="font-semibold text-gray-900">{avgRating}</span>
              <span className="text-sm text-gray-500">({reviewCount} reviews)</span>
            </div>
          )}
          
          <h2 className="text-3xl font-bold text-gray-900">{recipe.title}</h2>
          <p className="text-gray-600">
            {recipe.description && recipe.description.trim() !== ''
              ? recipe.description
              : 'No description provided.'}
          </p>

          <div className="flex flex-wrap gap-3 items-center">
            {recipe.vegan && (
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-sm">
                <FontAwesomeIcon icon={faLeaf} /> Vegan
              </span>
            )}
            {recipe.highProtein && (
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-sm">
                High Protein
              </span>
            )}
            {recipe.budgetFriendly && (
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-50 text-green-700 text-sm">
                <FontAwesomeIcon icon={faDollarSign} /> Budget-Friendly
              </span>
            )}
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 text-orange-700 text-sm">
              <FontAwesomeIcon icon={faFire} />
              {recipe.calories ? `${recipe.calories} kcal` : 'Calories n/a'}
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-50 text-sky-700 text-sm">
              <FontAwesomeIcon icon={faClock} />
              {recipe.cookingMinutes ? `${recipe.cookingMinutes} min` : 'Time n/a'}
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-50 text-gray-700 text-sm">
              <FontAwesomeIcon icon={faUser} />
              {recipe.portionSize ? `${recipe.portionSize} servings` : 'Servings n/a'}
            </span>
            {level > 0 && (
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-sm">
                {[...Array(3)].map((_, idx) => (
                  <FontAwesomeIcon
                    key={idx}
                    icon={faUtensils}
                    className={idx < level ? 'text-amber-600' : 'text-amber-200'}
                  />
                ))}
                Difficulty
              </span>
            )}
            {recipe.cuisine && (
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-50 text-purple-700 text-sm">
                {recipe.cuisine}
              </span>
            )}
          </div>
        </div>

        <div className="relative flex justify-center md:justify-end">
          <div className="transform rotate-2">{/* slight tilt to the right */}
            <img
              src={recipe.imageURL}
              alt={recipe.title}
              className="w-full max-w-xl h-auto max-h-[420px] object-cover rounded-3xl shadow-xl"
            />
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-[2fr_1fr] gap-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
          <h3 className="section-title flex items-center gap-2 text-lg font-semibold text-gray-900">
            <FontAwesomeIcon icon={faBookOpen} /> Method
          </h3>
          <ol className="list-decimal list-outside pl-5 space-y-3 text-gray-700">
            {Array.isArray(recipe.instructions) && recipe.instructions.length > 0 ? (
              recipe.instructions.map((instruction, index) => (
                <li key={index}>{instruction}</li>
              ))
            ) : (
              <li>No instructions provided.</li>
            )}
          </ol>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-4">
          <h3 className="section-title flex items-center gap-2 text-lg font-semibold text-gray-900">
            <FontAwesomeIcon icon={faUtensils} /> Ingredients
          </h3>
          <div className="flex flex-wrap gap-2">
            {Array.isArray(recipe.ingredients) && recipe.ingredients.length > 0 ? (
              recipe.ingredients.map((ingredient, index) => (
                <button
                  key={index}
                  onClick={() => handleIngredientClick(index)}
                  className={`ingredient-tag ${
                    clickedIngredients.includes(index) ? 'clicked' : ''
                  } px-3 py-1 rounded-full border text-sm transition`}
                >
                  {ingredient}
                </button>
              ))
            ) : (
              <span className="text-gray-600">No ingredients listed.</span>
            )}
          </div>
        </div>
      </div>
<ReviewsSection recipeId={recipeId} />

      <RelatedRecipes currentId={recipeId} cuisine={recipe.cuisine} />

      
    </div>
  );
}

export default RecipeDetails;
